/**
 * Settings store — local preferences for dashboard + CNS configuration.
 *
 * All values are client-side defaults that will eventually be persisted
 * server-side via a settings API. For now, they live in Zustand with
 * localStorage persistence.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import type { LlmProvider } from '@assistmd/types';
import { settingsApi } from '@/lib/api/api-client';
import { useAuthStore } from './auth-store';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type { LlmProvider };
export type ThemeMode = 'dark' | 'light' | 'system';

export interface LlmSettings {
  provider: LlmProvider;
  modelId: string;
  maxTokens: number;
  temperature: number;
  maxConcurrency: number;
  taskTimeoutMs: number;
  maxRetries: number;
}

export interface AudioSettings {
  sampleRate: number;
  vadSensitivity: number;
  noiseReduction: boolean;
  diarization: boolean;
  maxRecordingDurationMs: number;
  deepgramModel: string;
}

export interface AutomationSettings {
  soapEnabled: boolean;
  summaryEnabled: boolean;
  smartFillEnabled: boolean;
  autopilotEnabled: boolean;
  cooldownMs: number;
  maxConcurrentJobs: number;
  jobTimeoutMs: number;
  retryAttempts: number;
}

export interface AppearanceSettings {
  theme: ThemeMode;
  compactMode: boolean;
  animationsEnabled: boolean;
  showRunConsole: boolean;
  showAlerts: boolean;
}

export interface FeatureFlags {
  workflows: boolean;
  voiceConsent: boolean;
  assistPatient: boolean;
  consultWizard: boolean;
  hashTask: boolean;
  armadaGo: boolean;
  looper: boolean;
  telemetry: boolean;
  debugPanel: boolean;
}

export interface SettingsState {
  llm: LlmSettings;
  audio: AudioSettings;
  automation: AutomationSettings;
  appearance: AppearanceSettings;
  features: FeatureFlags;

  // Actions
  updateLlm: (patch: Partial<LlmSettings>) => void;
  updateAudio: (patch: Partial<AudioSettings>) => void;
  updateAutomation: (patch: Partial<AutomationSettings>) => void;
  updateAppearance: (patch: Partial<AppearanceSettings>) => void;
  updateFeatures: (patch: Partial<FeatureFlags>) => void;
  resetSection: (section: 'llm' | 'audio' | 'automation' | 'appearance' | 'features') => void;
  resetAll: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Defaults (derived from @assistmd/config)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_LLM: LlmSettings = {
  provider: 'openai',
  modelId: 'gpt-4o',
  maxTokens: 4096,
  temperature: 0.3,
  maxConcurrency: 4,
  taskTimeoutMs: 60_000,
  maxRetries: 2,
};

const DEFAULT_AUDIO: AudioSettings = {
  sampleRate: 16_000,
  vadSensitivity: 0.5,
  noiseReduction: true,
  diarization: true,
  maxRecordingDurationMs: 7_200_000, // 2 hours
  deepgramModel: 'nova-2-medical',
};

const DEFAULT_AUTOMATION: AutomationSettings = {
  soapEnabled: true,
  summaryEnabled: true,
  smartFillEnabled: true,
  autopilotEnabled: false,
  cooldownMs: 5_000,
  maxConcurrentJobs: 3,
  jobTimeoutMs: 120_000,
  retryAttempts: 2,
};

const DEFAULT_APPEARANCE: AppearanceSettings = {
  theme: 'dark',
  compactMode: false,
  animationsEnabled: true,
  showRunConsole: true,
  showAlerts: true,
};

const DEFAULT_FEATURES: FeatureFlags = {
  workflows: true,
  voiceConsent: true,
  assistPatient: true,
  consultWizard: true,
  hashTask: true,
  armadaGo: true,
  looper: true,
  telemetry: true,
  debugPanel: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// Server sync helpers
// ─────────────────────────────────────────────────────────────────────────────

let syncTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_DEBOUNCE_MS = 500;

let isHydrating = false;

function debouncedSync(state: SettingsState): void {
  if (isHydrating) return; // Don't sync during hydration
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    const { llm, audio, automation, appearance, features } = state;
    const doctorId = useAuthStore.getState().doctorId ?? 'system';
    settingsApi.update(doctorId, { llm, audio, automation, appearance, features }).catch(() => {});
  }, SYNC_DEBOUNCE_MS);
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useSettingsStore = create<SettingsState>()(
  persist(
    immer((set) => ({
      llm: { ...DEFAULT_LLM },
      audio: { ...DEFAULT_AUDIO },
      automation: { ...DEFAULT_AUTOMATION },
      appearance: { ...DEFAULT_APPEARANCE },
      features: { ...DEFAULT_FEATURES },

      updateLlm: (patch) => {
        set((s) => { Object.assign(s.llm, patch); });
        debouncedSync(useSettingsStore.getState());
      },
      updateAudio: (patch) => {
        set((s) => { Object.assign(s.audio, patch); });
        debouncedSync(useSettingsStore.getState());
      },
      updateAutomation: (patch) => {
        set((s) => { Object.assign(s.automation, patch); });
        debouncedSync(useSettingsStore.getState());
      },
      updateAppearance: (patch) => {
        set((s) => { Object.assign(s.appearance, patch); });
        debouncedSync(useSettingsStore.getState());
      },
      updateFeatures: (patch) => {
        set((s) => { Object.assign(s.features, patch); });
        debouncedSync(useSettingsStore.getState());
      },
      resetSection: (section) => {
        set((s) => {
          switch (section) {
            case 'llm': s.llm = { ...DEFAULT_LLM }; break;
            case 'audio': s.audio = { ...DEFAULT_AUDIO }; break;
            case 'automation': s.automation = { ...DEFAULT_AUTOMATION }; break;
            case 'appearance': s.appearance = { ...DEFAULT_APPEARANCE }; break;
            case 'features': s.features = { ...DEFAULT_FEATURES }; break;
          }
        });
      },
      resetAll: () => {
        set((s) => {
          s.llm = { ...DEFAULT_LLM };
          s.audio = { ...DEFAULT_AUDIO };
          s.automation = { ...DEFAULT_AUTOMATION };
          s.appearance = { ...DEFAULT_APPEARANCE };
          s.features = { ...DEFAULT_FEATURES };
        });
      },
    })),
    {
      name: 'assistmd-settings',
      version: 1,
    },
  ),
);

// ─────────────────────────────────────────────────────────────────────────────
// Hydration from server
// ─────────────────────────────────────────────────────────────────────────────

export async function hydrateSettingsFromServer(doctorId: string = 'system'): Promise<void> {
  isHydrating = true;
  try {
    const remote = await settingsApi.get(doctorId);
    if (remote && Object.keys(remote).length > 0) {
      const store = useSettingsStore.getState();
      if (remote['llm']) store.updateLlm(remote['llm'] as Partial<LlmSettings>);
      if (remote['audio']) store.updateAudio(remote['audio'] as Partial<AudioSettings>);
      if (remote['automation']) store.updateAutomation(remote['automation'] as Partial<AutomationSettings>);
      if (remote['appearance']) store.updateAppearance(remote['appearance'] as Partial<AppearanceSettings>);
      if (remote['features']) store.updateFeatures(remote['features'] as Partial<FeatureFlags>);
    }
  } catch { /* use localStorage defaults on failure */ }
  finally {
    isHydrating = false;
  }
}
